// RR-1 (86d439tn0) — mirrors healthcare's ReferenceRangeResolver; both run __fixtures__/rr1-cases.json
import { labWallClockDate } from "./bloodTestShape";

export type RangeSex = "any" | "male" | "female";
export type PatientSex = "male" | "female" | "unknown";
export type RangeSource = "platform" | "lab" | null;
export type DerivedFlag = "H" | "L" | "C" | null;

export interface ReferenceRange {
	id: number;
	sex: RangeSex;
	age_min_years: number | null;
	age_max_years: number | null;
	min: number | null;
	max: number | null;
	critical_min: number | null;
	critical_max: number | null;
	source?: string | null;
	sort_order: number;
}

export interface RangeContext {
	rangesByType: Map<string, ReferenceRange[]>;
	sex: PatientSex;
	dob: string | null;
	// false only while the catalogue is still loading; absent means ready
	ready?: boolean;
}

export interface ResolvedRange {
	min: number | null;
	max: number | null;
	criticalMin: number | null;
	criticalMax: number | null;
	source: RangeSource;
	rangeId: number | null;
	display: string | null;
}

export interface RangeAnalyte {
	canonical_type?: string | null;
	collected_at?: string | null;
	reported_at?: string | null;
	lab_reference_range?: string | null;
	lab_reference_min?: number | string | null;
	lab_reference_max?: number | string | null;
}

export const EMPTY_RANGE_CONTEXT: RangeContext = { rangesByType: new Map(), sex: "unknown", dob: null };

const MALE = ["male", "m", "man", "boy"];
const FEMALE = ["female", "f", "woman", "girl"];

const toNumber = (value: unknown): number | null => {
	if (value === null || value === undefined || value === "") return null;
	const n = typeof value === "number" ? value : Number(String(value).trim());
	return Number.isFinite(n) ? n : null;
};

const sexToken = (value?: string | null): PatientSex => {
	const v = (value ?? "").trim().toLowerCase();
	if (MALE.includes(v)) return "male";
	if (FEMALE.includes(v)) return "female";
	return "unknown";
};

export const normaliseSex = (sex?: string | null, gender?: string | null): PatientSex => {
	const fromSex = sexToken(sex);
	return fromSex === "unknown" ? sexToken(gender) : fromSex;
};

// Calendar day only — never `new Date(iso)`, which reads lab wall clock as UTC and shifts the day
const calendarDayUtc = (value?: string | Date | null): number | null => {
	if (!value) return null;
	const raw = value instanceof Date ? null : String(value).trim();
	const m = raw?.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	const loose = labWallClockDate(value);
	return loose ? Date.UTC(loose.getFullYear(), loose.getMonth(), loose.getDate()) : null;
};

export const ageAtCollection = (dob?: string | Date | null, collectedAt?: string | Date | null): number | null => {
	const born = calendarDayUtc(dob);
	const collected = calendarDayUtc(collectedAt);
	if (born === null || collected === null) return null;
	return (collected - born) / 86_400_000 / 365.25;
};

const formatNumber = (value: number): string => {
	const s = value.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
	return s === "-0" ? "0" : s;
};

export const formatRange = (min: number | null, max: number | null): string | null => {
	if (min !== null && max !== null) return `${formatNumber(min)}–${formatNumber(max)}`;
	if (min !== null) return `> ${formatNumber(min)}`;
	if (max !== null) return `< ${formatNumber(max)}`;
	return null;
};

const NUM = "(-?\\d+(?:\\.\\d+)?)";
const PAIR = new RegExp(`^${NUM}\\s*-\\s*${NUM}$`);
const PIPE = new RegExp(`^${NUM}\\s*\\|\\s*${NUM}$`);
const LT = new RegExp(`^<\\s*${NUM}$`);
const GT = new RegExp(`^>\\s*${NUM}$`);

export const parseLabRange = (value?: string | null): { min: number | null; max: number | null } => {
	const s = (value ?? "").trim();
	if (!s) return { min: null, max: null };
	const pair = s.match(PAIR) ?? s.match(PIPE);
	if (pair) return { min: Number(pair[1]), max: Number(pair[2]) };
	const lt = s.match(LT);
	if (lt) return { min: null, max: Number(lt[1]) };
	const gt = s.match(GT);
	if (gt) return { min: Number(gt[1]), max: null };
	return { min: null, max: null };
};

const orderBefore = (a: ReferenceRange, b: ReferenceRange): boolean => {
	const sortA = a.sort_order ?? 0;
	const sortB = b.sort_order ?? 0;
	if (sortA !== sortB) return sortA < sortB;
	return (a.id ?? Number.MAX_SAFE_INTEGER) < (b.id ?? Number.MAX_SAFE_INTEGER);
};

export const selectReferenceRange = (ranges: ReferenceRange[], sex: PatientSex, age: number | null): ReferenceRange | null => {
	let best: ReferenceRange | null = null;
	let bestRank = -1;

	for (const row of ranges) {
		const rowSex = row.sex ?? "any";
		if (rowSex !== "any" && rowSex !== sex) continue;

		const ageMin = toNumber(row.age_min_years);
		const ageMax = toNumber(row.age_max_years);
		const banded = ageMin !== null || ageMax !== null;
		if (banded && (age === null || (ageMin !== null && age < ageMin) || (ageMax !== null && age >= ageMax))) continue;

		const rank = (rowSex !== "any" ? 2 : 0) + (banded ? 1 : 0);
		if (best === null || rank > bestRank || (rank === bestRank && orderBefore(row, best))) {
			best = row;
			bestRank = rank;
		}
	}

	return best;
};

const NO_RANGE: ResolvedRange = { min: null, max: null, criticalMin: null, criticalMax: null, source: null, rangeId: null, display: null };

export const resolveFromRanges = (ranges: ReferenceRange[], sex: PatientSex, age: number | null, analyte: RangeAnalyte): ResolvedRange => {
	const best = selectReferenceRange(ranges, sex, age);
	if (best) {
		const min = toNumber(best.min);
		const max = toNumber(best.max);
		return {
			min,
			max,
			criticalMin: toNumber(best.critical_min),
			criticalMax: toNumber(best.critical_max),
			source: "platform",
			rangeId: best.id ?? null,
			display: formatRange(min, max),
		};
	}

	let labMin = toNumber(analyte.lab_reference_min);
	let labMax = toNumber(analyte.lab_reference_max);
	if (labMin === null && labMax === null) ({ min: labMin, max: labMax } = parseLabRange(analyte.lab_reference_range));

	if (labMin !== null || labMax !== null) {
		return { min: labMin, max: labMax, criticalMin: null, criticalMax: null, source: "lab", rangeId: null, display: formatRange(labMin, labMax) };
	}

	return NO_RANGE;
};

export const resolveRange = (analyte: RangeAnalyte, ctx: RangeContext): ResolvedRange => {
	// An unloaded catalogue must not fall through to the lab range and then flip once it arrives
	if (ctx.ready === false) return NO_RANGE;
	const canonical = (analyte.canonical_type ?? "").trim();
	const ranges = canonical ? ctx.rangesByType.get(canonical) ?? [] : [];
	const age = ageAtCollection(ctx.dob, analyte.collected_at ?? analyte.reported_at ?? null);
	return resolveFromRanges(ranges, ctx.sex, age, analyte);
};

export const deriveFlag = (numericValue: number | null, range: Pick<ResolvedRange, "min" | "max" | "criticalMin" | "criticalMax">): DerivedFlag => {
	if (numericValue === null || !Number.isFinite(numericValue)) return null;
	if ((range.criticalMax !== null && numericValue > range.criticalMax) || (range.criticalMin !== null && numericValue < range.criticalMin)) return "C";
	if (range.max !== null && numericValue > range.max) return "H";
	if (range.min !== null && numericValue < range.min) return "L";
	return null;
};
